import React from 'react';
import Contract from '../../../../../../../../../models/contract';

interface CustomerInfoProps {
  contract: Contract | null;
  className?: string;
}

/**
 * Composant qui affiche les informations du client (bénéficiaire)
 * Utilisé dans l'étape de qualification pour avoir le contexte client
 */
const CustomerInfo: React.FC<CustomerInfoProps> = ({ contract, className = '' }) => {
  if (!contract?.beneficiary) {
    return (
      <div className={`tw-bg-gray-50 tw-p-4 tw-rounded-lg tw-border tw-border-gray-200 ${className}`}>
        <div className="tw-p-3 tw-text-center">
          <h3 className="tw-text-lg tw-font-medium tw-text-gray-600">Informations client</h3>
          <p className="tw-text-gray-500">Aucune information client disponible</p>
        </div>
      </div>
    );
  }

  const { beneficiary } = contract;

  return (
    <div className={`tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-shadow-sm ${className}`}>
      <div className="tw-p-4 tw-border-b tw-border-gray-200">
        <h3 className="tw-text-lg tw-font-medium tw-flex tw-items-center tw-gap-2">
          <i className="fa-solid fa-user tw-text-blue-600"></i>
          Informations client
        </h3>
      </div>

      <div className="tw-p-4">
        {/* Nom et contact du client */}
        <div className="tw-mb-4">
          <h4 className="tw-text-sm tw-uppercase tw-text-gray-500 tw-mb-2">Identité et contact</h4>
          <p className="tw-text-lg tw-font-medium">
            {beneficiary.firstName} {beneficiary.lastName}
          </p>

          <div className="tw-mt-2 tw-space-y-1">
            {/* Email avec bouton de contact */}
            {beneficiary.email && (
              <div className="tw-flex tw-items-center tw-gap-2">
                <i className="fa-solid fa-envelope tw-text-gray-500 tw-w-5"></i>
                <a
                  href={`mailto:${beneficiary.email}`}
                  className="tw-text-blue-600 hover:tw-text-blue-800 tw-transition-colors"
                >
                  {beneficiary.email}
                </a>
              </div>
            )}

            {/* Téléphone avec bouton de contact */}
            {beneficiary.phoneNumber && (
              <div className="tw-flex tw-items-center tw-gap-2">
                <i className="fa-solid fa-phone tw-text-gray-500 tw-w-5"></i>
                <a
                  href={`tel:${beneficiary.phoneNumber.replace(/\s/g, '')}`}
                  className="tw-text-blue-600 hover:tw-text-blue-800 tw-transition-colors"
                >
                  {beneficiary.phoneNumber}
                </a>
              </div>
            )}

            {/* Téléphone 2 avec bouton de contact */}
            {beneficiary.phoneNumber2 && (
              <div className="tw-flex tw-items-center tw-gap-2">
                <i className="fa-solid fa-mobile-alt tw-text-gray-500 tw-w-5"></i>
                <a
                  href={`tel:${beneficiary.phoneNumber2.replace(/\s/g, '')}`}
                  className="tw-text-blue-600 hover:tw-text-blue-800 tw-transition-colors"
                >
                  {beneficiary.phoneNumber2}
                </a>
              </div>
            )}
          </div>
        </div>

        {/* Adresse du client */}
        <div>
          <h4 className="tw-text-sm tw-uppercase tw-text-gray-500 tw-mb-2">Adresse</h4>
          <div className="tw-space-y-1 tw-text-gray-700">
            <p>{beneficiary.address}</p>
            {beneficiary.address2 && <p>{beneficiary.address2}</p>}
            <p>{beneficiary.cp} {beneficiary.city}</p>
          </div>
        </div>

        {/* Actions rapides */}
        <div className="tw-mt-6 tw-flex tw-flex-wrap tw-gap-2">
          {beneficiary.email && (
            <a
              href={`mailto:${beneficiary.email}`}
              className="tw-inline-flex tw-items-center tw-gap-1 tw-px-3 tw-py-1 tw-bg-blue-50 tw-text-blue-700 tw-rounded-md tw-text-sm hover:tw-bg-blue-100 tw-transition-colors"
            >
              <i className="fa-solid fa-envelope"></i>
              Envoyer un email
            </a>
          )}

          {beneficiary.phoneNumber && (
            <a
              href={`tel:${beneficiary.phoneNumber.replace(/\s/g, '')}`}
              className="tw-inline-flex tw-items-center tw-gap-1 tw-px-3 tw-py-1 tw-bg-green-50 tw-text-green-700 tw-rounded-md tw-text-sm hover:tw-bg-green-100 tw-transition-colors"
            >
              <i className="fa-solid fa-phone"></i>
              Appeler
            </a>
          )}
        </div>

        {/* Info-bulle d'aide */}
        <div className="tw-mt-4 tw-bg-blue-50 tw-p-3 tw-rounded-lg tw-text-sm tw-text-blue-700 tw-flex tw-items-start tw-gap-2">
          <div className="tw-text-blue-600 tw-mt-0.5">
            <i className="fa-solid fa-circle-info"></i>
          </div>
          <div>
            <p>Ces informations permettent de contacter le client si nécessaire durant le traitement du dossier.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerInfo;